import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { seekSupabase } from '@/integrations/supabase/seekClient';
import { useSeekAuth } from '@/contexts/SeekAuthContext';
import { useToast } from '@/hooks/use-toast';
import type { Database, Json } from '@/integrations/supabase/types';

export type CandidateProfileTable =
  | 'candidate_profile_skills'
  | 'candidate_profile_experiences'
  | 'candidate_profile_education'
  | 'candidate_profile_certifications'
  | 'candidate_profile_languages'
  | 'candidate_profile_links';

type CandidateTables = Database['public']['Tables'];
type SkillInput = CandidateTables['candidate_profile_skills']['Insert'];
type ExperienceInput = CandidateTables['candidate_profile_experiences']['Insert'];
type EducationInput = CandidateTables['candidate_profile_education']['Insert'];
type CertificationInput = CandidateTables['candidate_profile_certifications']['Insert'];
type LanguageInput = CandidateTables['candidate_profile_languages']['Insert'];
type LinkInput = CandidateTables['candidate_profile_links']['Insert'];

function isString(value: unknown): value is string {
  return typeof value === 'string';
}

function isNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isBoolean(value: unknown): value is boolean {
  return typeof value === 'boolean';
}

function isOptionalNullable(value: unknown, validator: (candidate: unknown) => boolean): boolean {
  return value === undefined || value === null || validator(value);
}

function isOptional(value: unknown, validator: (candidate: unknown) => boolean): boolean {
  return value === undefined || validator(value);
}

function isJson(value: unknown): value is Json {
  if (value === null || isString(value) || isNumber(value) || isBoolean(value)) return true;
  if (Array.isArray(value)) return value.every(isJson);
  if (typeof value !== 'object') return false;
  return Object.values(value).every((item) => item === undefined || isJson(item));
}

function isSkillInput(data: Record<string, unknown>): data is SkillInput {
  return isString(data.candidate_profile_id)
    && isString(data.skill_name)
    && isOptional(data.created_at, isString)
    && isOptional(data.id, isString)
    && isOptionalNullable(data.proficiency_level, isString)
    && isOptionalNullable(data.source_type, isString)
    && isOptional(data.updated_at, isString)
    && isOptionalNullable(data.years_experience, isNumber);
}

function isExperienceInput(data: Record<string, unknown>): data is ExperienceInput {
  return isString(data.candidate_profile_id)
    && isString(data.title)
    && isOptionalNullable(data.achievements_json, isJson)
    && isOptional(data.created_at, isString)
    && isOptionalNullable(data.description, isString)
    && isOptionalNullable(data.display_order, isNumber)
    && isOptionalNullable(data.employer_name, isString)
    && isOptionalNullable(data.employment_type, isString)
    && isOptionalNullable(data.end_date, isString)
    && isOptional(data.id, isString)
    && isOptionalNullable(data.is_current, isBoolean)
    && isOptionalNullable(data.location, isString)
    && isOptionalNullable(data.source_type, isString)
    && isOptionalNullable(data.start_date, isString)
    && isOptional(data.updated_at, isString);
}

function isEducationInput(data: Record<string, unknown>): data is EducationInput {
  return isString(data.candidate_profile_id)
    && isString(data.institution_name)
    && isOptional(data.created_at, isString)
    && isOptionalNullable(data.degree, isString)
    && isOptionalNullable(data.description, isString)
    && isOptionalNullable(data.display_order, isNumber)
    && isOptionalNullable(data.end_date, isString)
    && isOptionalNullable(data.field_of_study, isString)
    && isOptionalNullable(data.grade, isString)
    && isOptionalNullable(data.grade_type, isString)
    && isOptional(data.id, isString)
    && isOptionalNullable(data.location, isString)
    && isOptionalNullable(data.source_type, isString)
    && isOptionalNullable(data.start_date, isString)
    && isOptional(data.updated_at, isString);
}

function isCertificationInput(data: Record<string, unknown>): data is CertificationInput {
  return isString(data.candidate_profile_id)
    && isString(data.certification_name)
    && isOptional(data.created_at, isString)
    && isOptionalNullable(data.credential_id, isString)
    && isOptionalNullable(data.credential_url, isString)
    && isOptionalNullable(data.expiry_date, isString)
    && isOptional(data.id, isString)
    && isOptionalNullable(data.issue_date, isString)
    && isOptionalNullable(data.issuing_organization, isString)
    && isOptionalNullable(data.source_type, isString)
    && isOptional(data.updated_at, isString);
}

function isLanguageInput(data: Record<string, unknown>): data is LanguageInput {
  return isString(data.candidate_profile_id)
    && isString(data.language_name)
    && isOptional(data.created_at, isString)
    && isOptional(data.id, isString)
    && isOptionalNullable(data.proficiency_level, isString)
    && isOptionalNullable(data.source_type, isString)
    && isOptional(data.updated_at, isString);
}

function isLinkInput(data: Record<string, unknown>): data is LinkInput {
  return isString(data.candidate_profile_id)
    && isString(data.link_type)
    && isString(data.url)
    && isOptional(data.created_at, isString)
    && isOptional(data.id, isString)
    && isOptionalNullable(data.label, isString)
    && isOptional(data.updated_at, isString);
}

function invalidRecord(table: CandidateProfileTable): Error {
  return new Error(`Invalid candidate profile record for ${table}`);
}

async function insertCandidateProfileRecord(table: CandidateProfileTable, data: Record<string, unknown>) {
  switch (table) {
    case 'candidate_profile_skills':
      if (!isSkillInput(data)) throw invalidRecord(table);
      return seekSupabase.from('candidate_profile_skills').insert(data);
    case 'candidate_profile_experiences':
      if (!isExperienceInput(data)) throw invalidRecord(table);
      return seekSupabase.from('candidate_profile_experiences').insert(data);
    case 'candidate_profile_education':
      if (!isEducationInput(data)) throw invalidRecord(table);
      return seekSupabase.from('candidate_profile_education').insert(data);
    case 'candidate_profile_certifications':
      if (!isCertificationInput(data)) throw invalidRecord(table);
      return seekSupabase.from('candidate_profile_certifications').insert(data);
    case 'candidate_profile_languages':
      if (!isLanguageInput(data)) throw invalidRecord(table);
      return seekSupabase.from('candidate_profile_languages').insert(data);
    case 'candidate_profile_links':
      if (!isLinkInput(data)) throw invalidRecord(table);
      return seekSupabase.from('candidate_profile_links').insert(data);
  }
}

async function updateCandidateProfileRecord(table: CandidateProfileTable, id: string, data: Record<string, unknown>) {
  switch (table) {
    case 'candidate_profile_skills':
      if (!isSkillInput(data)) throw invalidRecord(table);
      return seekSupabase.from('candidate_profile_skills').update(data).eq('id', id);
    case 'candidate_profile_experiences':
      if (!isExperienceInput(data)) throw invalidRecord(table);
      return seekSupabase.from('candidate_profile_experiences').update(data).eq('id', id);
    case 'candidate_profile_education':
      if (!isEducationInput(data)) throw invalidRecord(table);
      return seekSupabase.from('candidate_profile_education').update(data).eq('id', id);
    case 'candidate_profile_certifications':
      if (!isCertificationInput(data)) throw invalidRecord(table);
      return seekSupabase.from('candidate_profile_certifications').update(data).eq('id', id);
    case 'candidate_profile_languages':
      if (!isLanguageInput(data)) throw invalidRecord(table);
      return seekSupabase.from('candidate_profile_languages').update(data).eq('id', id);
    case 'candidate_profile_links':
      if (!isLinkInput(data)) throw invalidRecord(table);
      return seekSupabase.from('candidate_profile_links').update(data).eq('id', id);
  }
}

async function deleteCandidateProfileRecord(table: CandidateProfileTable, id: string) {
  switch (table) {
    case 'candidate_profile_skills':
      return seekSupabase.from('candidate_profile_skills').delete().eq('id', id);
    case 'candidate_profile_experiences':
      return seekSupabase.from('candidate_profile_experiences').delete().eq('id', id);
    case 'candidate_profile_education':
      return seekSupabase.from('candidate_profile_education').delete().eq('id', id);
    case 'candidate_profile_certifications':
      return seekSupabase.from('candidate_profile_certifications').delete().eq('id', id);
    case 'candidate_profile_languages':
      return seekSupabase.from('candidate_profile_languages').delete().eq('id', id);
    case 'candidate_profile_links':
      return seekSupabase.from('candidate_profile_links').delete().eq('id', id);
  }
}

export function useCandidateProfile() {
  const { user } = useSeekAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const uid = user?.id;

  const profileQuery = useQuery({
    queryKey: ['candidate-profile', uid],
    queryFn: async () => {
      const { data, error } = await seekSupabase
        .from('candidate_profiles')
        .select('*')
        .eq('candidate_id', uid!)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!uid,
    retry: 2,
  });

  const experiencesQuery = useQuery({
    queryKey: ['candidate-experiences', uid],
    queryFn: async () => {
      const profileId = profileQuery.data?.id;
      if (!profileId) return [];
      const { data, error } = await seekSupabase
        .from('candidate_profile_experiences')
        .select('*')
        .eq('candidate_profile_id', profileId)
        .order('display_order', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!profileQuery.data?.id,
  });

  const educationQuery = useQuery({
    queryKey: ['candidate-education', uid],
    queryFn: async () => {
      const profileId = profileQuery.data?.id;
      if (!profileId) return [];
      const { data, error } = await seekSupabase
        .from('candidate_profile_education')
        .select('*')
        .eq('candidate_profile_id', profileId)
        .order('display_order', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!profileQuery.data?.id,
  });

  const skillsQuery = useQuery({
    queryKey: ['candidate-skills', uid],
    queryFn: async () => {
      const profileId = profileQuery.data?.id;
      if (!profileId) return [];
      const { data, error } = await seekSupabase
        .from('candidate_profile_skills')
        .select('*')
        .eq('candidate_profile_id', profileId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!profileQuery.data?.id,
  });

  const languagesQuery = useQuery({
    queryKey: ['candidate-languages', uid],
    queryFn: async () => {
      const profileId = profileQuery.data?.id;
      if (!profileId) return [];
      const { data, error } = await seekSupabase
        .from('candidate_profile_languages')
        .select('*')
        .eq('candidate_profile_id', profileId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!profileQuery.data?.id,
  });

  const certificationsQuery = useQuery({
    queryKey: ['candidate-certifications', uid],
    queryFn: async () => {
      const profileId = profileQuery.data?.id;
      if (!profileId) return [];
      const { data, error } = await seekSupabase
        .from('candidate_profile_certifications')
        .select('*')
        .eq('candidate_profile_id', profileId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!profileQuery.data?.id,
  });

  const linksQuery = useQuery({
    queryKey: ['candidate-links', uid],
    queryFn: async () => {
      const profileId = profileQuery.data?.id;
      if (!profileId) return [];
      const { data, error } = await seekSupabase
        .from('candidate_profile_links')
        .select('*')
        .eq('candidate_profile_id', profileId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!profileQuery.data?.id,
  });

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['candidate-profile', uid] });
    qc.invalidateQueries({ queryKey: ['candidate-experiences', uid] });
    qc.invalidateQueries({ queryKey: ['candidate-education', uid] });
    qc.invalidateQueries({ queryKey: ['candidate-skills', uid] });
    qc.invalidateQueries({ queryKey: ['candidate-languages', uid] });
    qc.invalidateQueries({ queryKey: ['candidate-certifications', uid] });
    qc.invalidateQueries({ queryKey: ['candidate-links', uid] });
    // Invalidate match scores so they recalculate with updated profile
    qc.invalidateQueries({ queryKey: ['job-match-scores', uid] });
    qc.invalidateQueries({ queryKey: ['recommended-jobs', uid] });
  };

  const updateProfile = useMutation({
    mutationFn: async (updates: Record<string, unknown>) => {
      const { phone, full_name, email, ...safeUpdates } = updates as Record<string, unknown>;
      const { error } = await seekSupabase
        .from('candidate_profiles')
        .update(safeUpdates)
        .eq('candidate_id', uid!);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateAll();
      toast({ title: 'Profile updated' });
    },
    onError: () => toast({ title: 'Failed to update profile', variant: 'destructive' }),
  });

  // Generic CRUD helpers for sub-tables
  const addRecord = useMutation({
    mutationFn: async ({ table, data }: { table: CandidateProfileTable; data: Record<string, unknown> }) => {
      const { error } = await insertCandidateProfileRecord(table, data);
      if (error) throw error;
    },
    onSuccess: () => { invalidateAll(); toast({ title: 'Added successfully' }); },
    onError: () => toast({ title: 'Failed to add', variant: 'destructive' }),
  });

  const updateRecord = useMutation({
    mutationFn: async ({ table, id, data }: { table: CandidateProfileTable; id: string; data: Record<string, unknown> }) => {
      const { error } = await updateCandidateProfileRecord(table, id, data);
      if (error) throw error;
    },
    onSuccess: () => { invalidateAll(); toast({ title: 'Updated successfully' }); },
    onError: () => toast({ title: 'Failed to update', variant: 'destructive' }),
  });

  const deleteRecord = useMutation({
    mutationFn: async ({ table, id }: { table: CandidateProfileTable; id: string }) => {
      const { error } = await deleteCandidateProfileRecord(table, id);
      if (error) throw error;
    },
    onSuccess: () => { invalidateAll(); toast({ title: 'Deleted successfully' }); },
    onError: () => toast({ title: 'Failed to delete', variant: 'destructive' }),
  });

  return {
    profile: profileQuery.data,
    profileLoading: profileQuery.isPending,
    profileError: profileQuery.error,
    refetchProfile: profileQuery.refetch,
    experiences: experiencesQuery.data ?? [],
    education: educationQuery.data ?? [],
    skills: skillsQuery.data ?? [],
    languages: languagesQuery.data ?? [],
    certifications: certificationsQuery.data ?? [],
    links: linksQuery.data ?? [],
    updateProfile,
    addRecord,
    updateRecord,
    deleteRecord,
    profileId: profileQuery.data?.id,
  };
}
